import { showToast } from './Common/Toast';
import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { supabase } from '../utils/supabaseClient';
import DocumentsService from '../services/DocumentsService';
import {
  PhotoIcon,
  CloudArrowUpIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

/**
 * JobPhotosTab - Photo upload and gallery for jobs
 * 
 * Industry Standard: Before/During/After photo workflow
 * Matches: ServiceTitan, Jobber, Housecall Pro
 * 
 * Features:
 * - Upload photos directly to job
 * - Tag photos as BEFORE/DURING/AFTER
 * - Photo gallery with preview
 * - Delete photos
 * - Auto-link to work_order_id
 */

const JobPhotosTab = ({ jobId, workOrderId }) => {
  const { user } = useUser();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [selectedTag, setSelectedTag] = useState('DURING'); // Default tag

  useEffect(() => {
    if (jobId || workOrderId) {
      loadPhotos();
    }
  }, [jobId, workOrderId]);

  const loadPhotos = async () => {
    try {
      setLoading(true);
      const id = workOrderId || jobId;
      
      // Photos are attachments with an image file_type (20260818b). Both live
      // file_type conventions are matched — bare extensions (what this app writes)
      // and MIME (what the portal wrote) — because matching one drops the other.
      const { data, error } = await supabase
        .from('attachments')
        .select('*')
        .eq('work_order_id', id)
        .or(`file_type.in.(${DocumentsService.IMAGE_FILE_TYPES.join(',')}),file_type.like.image/*`)
        .order('uploaded_at', { ascending: false });

      if (error) {
        console.error('Error loading photos:', error);
        return;
      }

      // #2987: company files are private — one batch of short-lived signed URLs. This used
      // getPublicUrl, and fell back to the raw stored value (a bare path that 404s in an <img>).
      // reuse: the grid loads every image at once and the lightbox shows the same URL, so a URL
      // issued on an earlier visit is served from the browser cache instead of re-downloaded.
      const signed = await DocumentsService.getSignedUrls((data || []).map((p) => p.file_url), 3600, { reuse: true });
      const photosWithUrls = (data || []).map((photo) => ({
        ...photo,
        // attachments stores an auto_tags ARRAY and file_url; this gallery is
        // built around the singular tag/photo_url job_photos used. Normalise
        // once here so the markup below stays as it is.
        tag: photo.auto_tags?.[0] || 'DURING',
        photo_url: photo.file_url,
        displayUrl: signed[photo.file_url] || null
      }));

      setPhotos(photosWithUrls);
    } catch (error) {
      console.error('Error loading photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const id = workOrderId || jobId;
      
      for (const file of files) {
        // Only allow images
        if (!file.type.startsWith('image/')) {
          showToast('info', `${file.name} is not an image file`);
          continue;
        }

        // Upload photo with selected tag
        await DocumentsService.uploadJobPhoto(
          user.company_id,
          id,
          file,
          user.id,
          [selectedTag] // Tag as BEFORE/DURING/AFTER
        );
      }

      // Reload photos
      await loadPhotos();
      
      // Reset file input
      e.target.value = '';
    } catch (error) {
      console.error('Error uploading photos:', error);
      showToast('error', 'Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      // companyId was omitted here, so the lookup filtered on company_id=eq.undefined
      // and every delete from this tab threw 'Photo not found'.
      await DocumentsService.deleteJobPhoto(photoId, user.company_id);
      await loadPhotos();
    } catch (error) {
      console.error('Error deleting photo:', error);
      showToast('error', 'Failed to delete photo');
    }
  };

  const getTagColor = (tag) => {
    switch (tag?.toUpperCase()) {
      case 'BEFORE':
        return 'bg-blue-100 text-blue-800';
      case 'DURING':
        return 'bg-yellow-100 text-yellow-800';
      case 'AFTER':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTagIcon = (tag) => {
    switch (tag?.toUpperCase()) {
      case 'BEFORE':
        return ClockIcon;
      case 'AFTER':
        return CheckCircleIcon;
      default:
        return PhotoIcon;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-gray-50 rounded-lg p-6 border-2 border-dashed border-gray-300">
        <div className="mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <CloudArrowUpIcon className="w-5 h-5 text-primary-600" />
              Upload Photos
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Upload photos from your computer
            </p>
          </div>
        </div>

        {/* Tag Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Photo Type
          </label>
          <div className="flex gap-2">
            {['BEFORE', 'DURING', 'AFTER'].map((tag) => {
              const TagIcon = getTagIcon(tag);
              return (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                    selectedTag === tag
                      ? 'border-primary-600 bg-primary-50 text-primary-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  <TagIcon className="w-4 h-4" />
                  {tag}
                </button>
              );
            })}
          </div>
        </div>

        {/* File Input */}
        <div className="relative">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileUpload}
            disabled={uploading}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-lg file:border-0
              file:text-sm file:font-semibold
              file:bg-primary-50 file:text-primary-700
              hover:file:bg-primary-100
              disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {uploading && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
              <div className="flex items-center gap-2 text-primary-600">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                <span className="text-sm font-medium">Uploading...</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Photos Grid */}
      {photos.length === 0 ? (
        <div className="text-center py-12">
          <PhotoIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No photos yet</p>
          <p className="text-sm text-gray-400 mt-1">Upload photos to document this job</p>
        </div>
      ) : (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Photos ({photos.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((photo) => {
              const TagIcon = getTagIcon(photo.tag);
              return (
                <div
                  key={photo.id}
                  className="relative group rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow"
                >
                  {/* Photo */}
                  <div
                    className="aspect-square bg-gray-100 cursor-pointer"
                    onClick={() => setPreviewPhoto(photo)}
                  >
                    <img
                      src={photo.displayUrl || undefined}
                      alt={`Job photo - ${photo.tag}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  {/* Tag Badge */}
                  <div className="absolute top-2 left-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${getTagColor(photo.tag)}`}>
                      <TagIcon className="w-3 h-3" />
                      {photo.tag}
                    </span>
                  </div>

                  {/* Delete Button */}
                  <button
                    onClick={() => handleDeletePhoto(photo.id)}
                    className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                    title="Delete photo"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>

                  {/* Zoom Icon */}
                  <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="p-1.5 bg-black bg-opacity-50 rounded-md">
                      <MagnifyingGlassIcon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Photo Preview Modal */}
      {previewPhoto && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewPhoto(null)}
        >
          <div className="relative max-w-6xl max-h-full">
            {/* Close Button */}
            <button
              onClick={() => setPreviewPhoto(null)}
              className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300"
            >
              <XMarkIcon className="w-8 h-8" />
            </button>

            {/* Photo */}
            <img
              src={previewPhoto.displayUrl || undefined}
              alt={`Job photo - ${previewPhoto.tag}`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Tag */}
            <div className="absolute top-4 left-4">
              <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${getTagColor(previewPhoto.tag)}`}>
                {React.createElement(getTagIcon(previewPhoto.tag), { className: 'w-4 h-4' })}
                {previewPhoto.tag}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobPhotosTab;


